<template>
  <div class="ccma-my-story">
    <!-- Full-screen loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('consultantCorner.myStory.LoadingStory') }}</p>
      </div>
    </div>

    <div v-if="showError" class="ccma-my-story-error">
      <h2>{{ t('consultantCorner.myStory.PageLoadError') }}</h2>
    </div>

    <!-- Error notification for save operations -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.myStory.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="saveStory">{{ t('consultantCorner.myStory.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.myStory.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <div v-else-if="!showError && !isLoading">
      <!-- Header Section -->
      <div class="story-header">
        <div class="first">
          <div class="story-title">{{ t('consultantCorner.myStory.MyStoryHeader') }}</div>
          <div class="story-subtitle mb-1">{{ t('consultantCorner.myStory.MyStorySubHeader') }}</div>
        </div>
        <div class="story-buttons">
          <button
            id="btnSave"
            class="baseBtn primaryBtn"
            type="button"
            @click="saveStory"
            :disabled="isSaving"
          >
            {{ isSaving ? t('consultantCorner.myStory.Saving') : t('consultantCorner.myStory.SaveStory') }}
          </button>
        </div>
      </div>

      <!-- My Story Form -->
      <div class="story-form-container">
        <div class="story-form-content">
          <form @submit.prevent="saveStory">
            <fieldset class="k-form-fieldset">

              <!-- Personal Story Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.myStory.PersonalStorySection') }}</h3>
                <div class="form-row">
                  <div class="form-field full-width">
                    <label class="form-label">{{ t('consultantCorner.myStory.PersonalStory') }}</label>
                    <RichTextEditor
                      v-model="storyData.storyText"
                      :has-error="!!errors.storyText"
                      :error-message="errors.storyText"
                      :placeholder="t('consultantCorner.myStory.StoryPlaceholder')"
                      height="400px"
                      @manual-change="handleStoryChange"
                    />
                  </div>
                </div>
              </div>

              <!-- Website Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.myStory.WebsiteSection') }}</h3>

                <!-- Generated Consultant URL -->
                <div v-if="personalUrl" class="form-row">
                  <div class="form-field full-width">
                    <label class="form-label">{{ t('consultantCorner.myStory.LinkToConsultantWeb') }}</label>
                    <div class="personal-url-display">
                      <a :href="personalUrl" target="_blank" rel="noopener noreferrer" class="personal-url-link">
                        {{ personalUrl }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { PwsProfileStoryGet, PwsProfileStorySave, type PwsProfileStoryViewModel, PwsProfileBusinessNameGet } from '../../api/ConsultantCorner'
import { useStoreLogger } from '../../logger'
import { Loader } from '@progress/kendo-vue-indicators'
import RichTextEditor from '../Controls/RichTextEditor.vue'
import { GetCountryCode, GetPwsBaseUrl } from '../../stores/SettingsStore'

const { t } = useI18n()
const logger = useStoreLogger()

// Reactive state
const showError = ref(false)
const isSaving = ref(false)
const saveError = ref(false)
const saveErrorMessage = ref('')
const saveSuccess = ref(false)
const isLoading = ref(false)

const storyData = reactive({
  storyText: '',
  consultantWebsite: ''
})

// URL generation state
const personalUrl = ref('')
const countryCode = ref('')
const businessName = ref('')

const errors = reactive({
  storyText: '',
  consultantWebsite: ''
})

// Methods
const validateUrl = (url: string): boolean => {
  if (!url) return true // URL is optional

  // Enhanced URL validation
  const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/
  return urlPattern.test(url)
}

const validateForm = (): boolean => {
  // Clear previous errors
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })

  let isValid = true

  // Validate website URL if provided
  if (storyData.consultantWebsite && !validateUrl(storyData.consultantWebsite)) {
    errors.consultantWebsite = t('consultantCorner.myStory.InvalidURL')
    isValid = false
  }

  return isValid
}

const loadBusinessName = async () => {
  try {
    const response = await PwsProfileBusinessNameGet() as any
    if (response && response.businessName) {
      businessName.value = response.businessName || ''
      await updatePersonalUrl()
    }
  } catch (error) {
    logger.error('Error loading business name:', error)
  }
}

const updatePersonalUrl = async () => {
  if (businessName.value && businessName.value.trim() !== '') {
    const businessNameForUrl = businessName.value.trim().toLowerCase().replace(/\s+/g, '-')
    const baseUrl = await GetPwsBaseUrl()
    personalUrl.value = `${baseUrl}${businessNameForUrl}`
  } else {
    personalUrl.value = ''
  }
}

const loadStoryData = async () => {
  try {
    isLoading.value = true
    showError.value = false

    const response = await PwsProfileStoryGet()

    // Map the API response to the form structure
    const item = response.item
    storyData.storyText = item.storyText || ''
    // Note: consultantWebsite is not part of the story API, keeping it separate for now
    storyData.consultantWebsite = ''

    logger.info('Story data loaded successfully', response)
  } catch (error) {
    logger.error('Error loading story data:', error)
    showError.value = true
  } finally {
    isLoading.value = false
  }
}

const saveStory = async () => {
  if (!validateForm()) {
    saveErrorMessage.value = t('consultantCorner.myStory.ValidationError')
    saveError.value = true
    return
  }

  try {
    isSaving.value = true
    clearNotifications()

    // Prepare the data for saving
    const saveData: PwsProfileStoryViewModel = {
      storyText: storyData.storyText,
      isStoryApproved: true
    }

    const response = await PwsProfileStorySave(saveData)

    // Check for ServiceStack ResponseStatus errors
    if (response && response.responseStatus) {
      const responseStatus = response.responseStatus

      // Handle specific error types
      if (responseStatus.errorCode || responseStatus.message) {
        const errorMessage = responseStatus.message || t('consultantCorner.myStory.SaveErrorGeneric')

        logger.error('ServiceStack error saving story:', {
          errorCode: responseStatus.errorCode,
          message: responseStatus.message,
          errors: responseStatus.errors
        })

        // Show user-friendly error message
        saveErrorMessage.value = getUserFriendlyErrorMessage(responseStatus)
        saveError.value = true
        return
      }
    }

    logger.info('Story saved successfully')

    // Show success notification
    saveSuccess.value = true

    // Auto-hide success message and refresh data after 2 seconds
    setTimeout(() => {
      clearNotifications()
      loadStoryData() // Refresh the story data
    }, 2000)

  } catch (error: any) {
    logger.error('Error saving story:', error)

    // Handle different types of errors
    if (error.response) {
      // HTTP error response
      const status = error.response.status
      const data = error.response.data

      if (data && data.ResponseStatus) {
        // ServiceStack error in HTTP response
        saveErrorMessage.value = getUserFriendlyErrorMessage(data.ResponseStatus)
      } else if (status >= 500) {
        saveErrorMessage.value = t('consultantCorner.myStory.SaveErrorGeneric')
      } else if (status === 400) {
        saveErrorMessage.value = t('consultantCorner.myStory.ValidationError')
      } else {
        saveErrorMessage.value = `${t('consultantCorner.myStory.SaveError')}: ${status}`
      }
    } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      // Network error
      saveErrorMessage.value = t('consultantCorner.myStory.NetworkError')
    } else {
      // Generic error
      saveErrorMessage.value = error.message || t('consultantCorner.myStory.SaveErrorGeneric')
    }

    saveError.value = true
  } finally {
    isSaving.value = false
  }
}

const getUserFriendlyErrorMessage = (responseStatus: any): string => {
  // Map common ServiceStack error codes to user-friendly messages
  const errorCode = responseStatus.ErrorCode
  const message = responseStatus.Message

  // Handle specific error codes
  switch (errorCode) {
    case 'ValidationException':
    case 'ArgumentException':
      return t('consultantCorner.myStory.ValidationError')
    case 'UnauthorizedAccessException':
    case 'AuthenticationException':
      return 'You are not authorized to perform this action. Please log in again.'
    case 'TimeoutException':
      return 'The request timed out. Please try again.'
    case 'SqlException':
    case 'DatabaseException':
      return 'A database error occurred. Please try again later.'
    default:
      // Use the message from the server if available, otherwise use generic message
      return message || t('consultantCorner.myStory.SaveErrorGeneric')
  }
}

const clearNotifications = () => {
  saveError.value = false
  saveSuccess.value = false
  saveErrorMessage.value = ''
}

const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}

const handleStoryChange = (value: string) => {
  storyData.storyText = value
  // Clear any existing error when user starts typing
  if (errors.storyText) {
    errors.storyText = ''
  }
}

// Lifecycle
onMounted(async () => {
  // Load country code first
  try {
    countryCode.value = await GetCountryCode() || ''
  } catch (error) {
    logger.error('Error getting country code:', error)
    countryCode.value = ''
  }

  // Load business name and generate URL
  await loadBusinessName()

  // Load story data
  loadStoryData()
})
</script>

<style lang="scss" scoped>
.ccma-my-story {
  padding: 1.5rem;
  width: 100%;
  position: relative;
}

.ccma-my-story-error {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-text {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #666;
}

/* Error notification styles */
.save-error-notification {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.error-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  gap: 0.75rem;
}

.error-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.error-details {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
  color: #721c24;
}

.error-details p {
  @include typography-body;
  margin: 0;
  color: #721c24;
}

.error-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.error-close:hover {
  background-color: rgba(#721c24, 0.1);
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #f5c6cb;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

/* Success notification styles */
.save-success-notification {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.success-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.75rem;
}

.success-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.success-message {
  flex: 1;
}

.success-message p {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #155724;
}

.success-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #155724;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.success-close:hover {
  background-color: rgba(#155724, 0.1);
}

/* Animation for notifications */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.story-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.story-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.25rem;
}

.story-subtitle {
  @include typography-body;
  color: #6c757d;
}

.story-buttons {
  display: flex;
  gap: 0.75rem;
}

.story-form-container {
  background: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  width: 100%;
}

.story-form-content {
  padding: 2rem;
  width: 100%;
  max-width: none;
}

.form-section {
  margin-bottom: 2rem;
}

.section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.form-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field.full-width {
  flex: 1 1 100%;
  width: 100%;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-input, .form-textarea {
  @include typography-body;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  font-family: inherit;
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
}

.form-input.error, .form-textarea.error {
  border-color: #dc3545;
}

.form-input.error:focus, .form-textarea.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

.baseBtn {
  @include typography-body;
  @include font-weight-medium;
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: $button-radius;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.baseBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


.mb-1 {
  margin-bottom: 0.25rem;
}

/* Personal URL styling */
.personal-url-display {
  padding: 0.75rem;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  @include typography-body;
  font-family: monospace;
  word-break: break-all;
  color: #495057;
  margin-bottom: 1rem;
}

.personal-url-link {
  color: #007bff;
  text-decoration: none;
  transition: color 0.15s ease-in-out;
}

.personal-url-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .ccma-my-story {
    padding: 1rem;
  }

  .story-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .story-buttons {
    margin-top: 0;
    justify-content: stretch;
    flex-direction: column;
    gap: 0.5rem;
  }

  .story-buttons .baseBtn {
    width: 100%;
  }

  .form-row {
    flex-direction: column;
    gap: 1rem;
  }

  .story-form-content {
    padding: 1.5rem 1rem;
  }

  .form-field {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .ccma-my-story {
    padding: 0.5rem;
  }

  .story-form-content {
    padding: 1rem;
  }

  .form-row {
    gap: 0.75rem;
  }
}
</style>
